import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";
import {
  deleteShipment,
  getShipmentList,
  exportShipments,
  patchShipment,
  downloadShipmentFile,
} from "../../api/imports";
import { getCatalogue, getStatusCatalogue } from "../../api/common/catalogues.ts";
import { getPurchaseOrderList } from "../../api/purchaseOrders";
import { Box, Button, Grid, TextField } from "@mui/material";
import ShipmentForm from "./ShipmentForm";
import dayjs from "dayjs";
import UpdateFileForm from "./ShipmentFilesTable";
import { FileDownload, Add as AddIcon, Search as SearchIcon } from "@mui/icons-material";
import ShipmentExportModal from "../../molecules/Imports/ShipmentExportModal";
import LGModal from "../../molecules/LGModal";
import MonthPicker from "../../atoms/Inputs/MonthPicker";
import YearPicker from "../../atoms/Inputs/YearPicker";
import { ManageShipmentContainers } from "./ManageShipmentContainers.tsx";
import { ManageShipmentPurchaseOrders } from "./ManageShipmentPurchaseOrders.tsx";
import { themeBalham } from "ag-grid-community";
import { useSnack } from "../../services/SnakbarProvider.js";
import { handleError } from "../../services/errorHandler.js";
import { createShipmentColumns } from "./ShipmentColumns.js";
import {
  UpdateDropdownEditor,
  TextCellRenderer,
  NumberCellRenderer,
  DateCellRenderer,
  BooleanCellRenderer,
  DropdownCellRenderer,
} from "../../atoms/Inputs/index.ts";
import { useAgGridPersistedPagination } from "../../hooks/usePersistedPagination";

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

export default function ShipmentCompleteTable({ companyId }) {
  const { openSnack } = useSnack();
  const { paginationPageSize, onPaginationChanged, restorePage } =
    useAgGridPersistedPagination();

  const [showCompleted, setShowCompleted] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [catalogues, setCatalogues] = useState({});
  const [statuses, setStatuses] = useState([]);

  const [month, setMonth] = useState(new Date().getMonth());
  const [year, setYear] = useState(new Date().getFullYear());

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const gridRef = useRef();

  const [documentsModalOpen, setDocumentsModalOpen] = useState(false);
  const [containersModalOpen, setContainersModalOpen] = useState(false);
  const [purchaseOrdersModalOpen, setPurchaseOrdersModalOpen] = useState(false);
  const [selectedShipment, setSelectedShipment] = useState(null);

  const onGridReady = useCallback((params) => {
    fetchData();
  }, []);

  const onFirstDataRendered = useCallback((params) => {
    params.api.autoSizeAllColumns();
    restorePage(params.api);
  }, [restorePage]);

  // Fetch catalogues for dropdowns
  const fetchCatalogues = async () => {
    try {
      const statusResponse = await getStatusCatalogue("SHIPMENT");
      setStatuses(statusResponse.data || []);

      const catalogueTypes = [
        "TARIFF_FRACTION",
        "SHIPMENT_TRANSPORT_TYPE",
        "PORT_DESTINATION",
        "FOREIGN_TRANSPORT",
        "FORWARDER",
        "PORT_OF_ORIGIN",
        "CUSTOMS_AGENT",
        "SHIPMENT_TYPE",
        "SHIPMENT_MODE",
        "FORWARDER_LAND",
      ];

      const catalogueData = {};
      for (const type of catalogueTypes) {
        const response = await getCatalogue({ CatalogueName: type, pageSize: 1000 });
        catalogueData[type] = response.data || [];
      }

      // Fetch purchase orders ordered by code descending
      try {
        const purchaseOrderResponse = await getPurchaseOrderList({
          companyId,
          pageSize: 1000,
          orderBy: "code",
          orderDirection: "desc",
          statusCodes:["PO_APROBADA"],
          isImport: true
        });
        catalogueData["PURCHASE_ORDERS"] = purchaseOrderResponse.data || [];
      } catch (error) {
        console.error("Error fetching purchase orders:", error);
        catalogueData["PURCHASE_ORDERS"] = [];
      }

      setCatalogues(catalogueData);
    } catch (error) {
      console.error("Error fetching catalogues:", error);
    }
  };

  useEffect(() => {
    fetchCatalogues();
  }, []);

  const setStatusColor = (statusCode) => {
    switch (statusCode) {
      case "EMBARCADO":
        return "default";
      case "EN_PUERTO":
        return "warning";
      case "EN_TRANSITO_LOCAL":
        return "info";
      case "ENTREGADO":
        return "success";
    }
  };

  const handleChange = () => {
    setShowCompleted(!showCompleted);
  };

  const handleExport = async (params) => {
    try {
      const response = await exportShipments(params);
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `shipments_export_${dayjs().format("DD-MM-YYYY")}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setExportModalOpen(false);
    } catch (error) {
      console.error("Error exporting shipments:", error);
    }
  };

  const handleDateChange = () => {
    let startDate = null;
    let endDate = null;
    if (month === 12) {
      startDate = dayjs(new Date(year, 0, 1)).format("DD/MM/YYYY");
      endDate = dayjs(new Date(year + 1, 0, 0)).format("DD/MM/YYYY");
    } else {
      startDate = dayjs(new Date(year, month + 1, 1)).format("DD/MM/YYYY");
      endDate = dayjs(new Date(year, month + 2, 0)).format("DD/MM/YYYY");
    }
    setStartDate(startDate);
    setEndDate(endDate);
  };

  const handleAddModalClose = () => {
    setAddModalOpen(false);
    fetchData(); // Refresh the data after adding a new shipment
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        companyId,
        showCompleted,
        startDate,
        endDate,
        pageSize: 1000,
        searchTerm: searchTerm || undefined,
      };
      const response = await getShipmentList(params);
      setRowData(response.data || []);
    } catch (error) {
      console.error("Error fetching shipments:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [companyId, showCompleted, startDate, endDate, searchTerm]);

  const handleCellUpdate = async (data, field, value) => {
    try {
      const updateData = {
        [field]: value,
      };
      await patchShipment(data.id, updateData);
      // Optionally refresh the data or show success message
    } catch (error) {
      console.error("Error updating shipment:", error);
      // Revert the change or show error message
    }
  };

  const columnDefs = useMemo(
    () => createShipmentColumns(catalogues, statuses),
    [catalogues]
  );

  const handleEdit = (data) => {
    // Handle edit action
    console.log("Edit:", data);
  };

  const handleDelete = async (id) => {
    try {
      await deleteShipment(id);
      fetchData(); // Refresh data after deletion
    } catch (error) {
      console.error("Error deleting shipment:", error);
    }
  };

  const handleCellValueChanged = async (params) => {
    const col = params.column.colId;
    const value = params.value;
    const id = params.data.id;

    try {
      await patchShipment(id, { [col]: value });
      // Refresh the table data to show updated values
      fetchData();
    } catch (error) {
      openSnack(handleError(error), "error");
    }
  };

  const handleCellEditingStopped = async (params) => {
    // Only proceed if the value actually changed
    if (params.value !== params.oldValue) {
      const col = params.column.colId;
      const value = params.value;
      const id = params.data.id;

      try {
        await patchShipment(id, { [col]: value });
        // Refresh the table data to show updated values
        fetchData();
      } catch (error) {
        openSnack(handleError(error), "error");
      }
    }
  };

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: true,
      filter: true,
      autoHeight: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      headerClass: "wrap-text-header",
      minWidth: 90,
    }),
    []
  );

  // Add custom CSS for header wrapping
  // useEffect(() => {
  //   const style = document.createElement("style");
  //   style.textContent = `
  //     .ag-cell {
  //       min-width: 90px !important;
  //     }
  //     .wrap-text-header .ag-header-cell-label {
  //       white-space: normal !important;
  //       line-height: 1.2;
  //     }
  //     .wrap-text-header .ag-header-cell-label span {
  //       white-space: normal !important;
  //       word-break: break-word;
  //     }
  //     .wrap-text-header .ag-header-cell {
  //       padding: 4px !important;
  //     }
  //     .ag-header-cell {
  //       line-height: 1.2;
  //     }
  //     .ag-header-cell-text {
  //       overflow: visible !important;
  //     }
  //   `;
  //   document.head.appendChild(style);
  //   return () => {
  //     // Check if the style element is still in the DOM before removing it
  //     if (style.parentNode === document.head) {
  //       document.head.removeChild(style);
  //     }
  //   };
  // }, []);

  // Define auto-size strategy
  const autoSizeStrategy = useMemo(
    () => ({
      type: "fitCellContents",
      defaultMinWidth: 50,
      skipHeader: false, // This will make the auto-sizing ignore header content
      columnLimits: [
        {
          colId: "actions",
          maxWidth: 150,
        },
        {
          colId: "ref",
          minWidth: 120,
        },
        {
          colId: "observations",
          minWidth: 200,
        },
      ],
    }),
    []
  );

  // Define components for AG Grid
  const components = useMemo(
    () => ({
      TextCellRenderer: TextCellRenderer,
      NumberCellRenderer: NumberCellRenderer,
      DateCellRenderer: DateCellRenderer,
      BooleanCellRenderer: BooleanCellRenderer,
      DropdownCellRenderer: DropdownCellRenderer,
      UpdateDropdownEditor: UpdateDropdownEditor,
    }),
    []
  );

  const handleViewDocuments = (data) => {
    setSelectedShipment(data);
    setDocumentsModalOpen(true);
  };

  const handleViewContainers = (data) => {
    setSelectedShipment(data);
    setContainersModalOpen(true);
  };

  const handleViewPurchaseOrders = (data) => {
    setSelectedShipment(data);
    setPurchaseOrdersModalOpen(true);
  };

  const handleDocumentsModalClose = () => {
    setDocumentsModalOpen(false);
    setSelectedShipment(null);
    fetchData(); // Refresh data after potential changes
  };

  const handleContainersModalClose = () => {
    setContainersModalOpen(false);
    setSelectedShipment(null);
    fetchData(); // Refresh data after potential changes
  };

  const handlePurchaseOrdersModalClose = () => {
    setPurchaseOrdersModalOpen(false);
    setSelectedShipment(null);
    fetchData(); // Refresh data after potential changes
  };

  // const handleDownload = async (id) => {
  //   let link = null;
  //   try {
  //     const response = await downloadShipmentFile(id);
  //     const contentDisposition = response.headers["content-disposition"];
  //     let fileName = "downloadedFile";

  //     if (contentDisposition && contentDisposition.indexOf("attachment") !== -1) {
  //       const matches = contentDisposition.match(/filename="([^"]*)"/);
  //       if (matches && matches[1]) {
  //         fileName = matches[1];
  //       }
  //     }

  //     const blob = new Blob([response.data], {
  //       type: response.headers["content-type"],
  //     });
  //     const url = window.URL.createObjectURL(blob);
  //     link = document.createElement("a");
  //     link.href = url;
  //     link.setAttribute("download", fileName);
  //     document.body.appendChild(link);
  //     link.click();
  //     window.URL.revokeObjectURL(url);
  //   } catch (error) {
  //     openSnack(handleError(error), "error");
  //   } finally {
  //     if (link && link.parentNode) {
  //       link.parentNode.removeChild(link);
  //     }
  //   }
  // };

  return (
    <Box width={"100%"}>
      <LGModal
        open={exportModalOpen}
        handleClose={() => setExportModalOpen(false)}
        header="Exportar Embarques"
      >
        <ShipmentExportModal companyId={companyId} onSubmit={handleExport} />
      </LGModal>

      <LGModal
        open={addModalOpen}
        handleClose={() => setAddModalOpen(false)}
        header="Agregar Embarque"
      >
        <ShipmentForm onSubmit={handleAddModalClose} companyId={companyId} />
      </LGModal>

      <LGModal
        open={documentsModalOpen}
        handleClose={handleDocumentsModalClose}
        header="Documentos"
      >
        <UpdateFileForm props={{ id: selectedShipment?.id }} />
      </LGModal>

      <LGModal
        open={containersModalOpen}
        handleClose={handleContainersModalClose}
        header="Contenedores"
      >
        <ManageShipmentContainers
          props={{
            id: selectedShipment?.id,
            onSubmit: handleContainersModalClose,
          }}
        />
      </LGModal>

      <LGModal
        open={purchaseOrdersModalOpen}
        handleClose={handlePurchaseOrdersModalClose}
        header="Órdenes de Compra"
      >
        <ManageShipmentPurchaseOrders
          props={{
            id: selectedShipment?.id,
            companyId: companyId,
            onSubmit: handlePurchaseOrdersModalClose,
          }}
        />
      </LGModal>

      <Grid container direction="row" spacing={2} mb={2}>
        <Grid item>
          <Button
            onClick={() => setAddModalOpen(true)}
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
          >
            Agregar
          </Button>
        </Grid>
        <Grid item>
          <Button onClick={handleChange}>
            {showCompleted ? "Mostrar completados" : "Ocultar completados"}
          </Button>
        </Grid>
        <Grid item>
          <Button
            onClick={() => setExportModalOpen(true)}
            variant="contained"
            startIcon={<FileDownload />}
          >
            Exportar CSV
          </Button>
        </Grid>
        <Grid item>
          <MonthPicker
            value={month}
            label="Mes"
            onChange={(e) => setMonth(e.target.value)}
          />
        </Grid>
        <Grid item>
          <YearPicker
            value={year}
            label="Año"
            onChange={(e) => setYear(e.target.value)}
          />
        </Grid>
        <Grid item>
          <Button variant="contained" onClick={handleDateChange}>
            Buscar
          </Button>
        </Grid>
      </Grid>

      <Box sx={{ mb: 2, maxWidth: 400 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Buscar..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: "action.active" }} />,
          }}
          sx={{ backgroundColor: "background.paper" }}
        />
      </Box>

      <div
        style={{
          height: 600,
          width: "100%",
        }}
      >
        <AgGridReact
          context={{
            // onDownload: handleDownload,
            onDelete: handleDelete,
            onViewDocuments: handleViewDocuments,
            onViewContainers: handleViewContainers,
            onViewPurchaseOrders: handleViewPurchaseOrders,
          }}
          theme={themeBalham}
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          components={components}
          pagination={true}
          paginationPageSize={paginationPageSize}
          paginationPageSizeSelector={[10, 20, 50, 100]}
          animateRows={true}
          loading={loading}
          stopEditingWhenCellsLoseFocus={true}
          singleClickEdit={true}
          onGridReady={onGridReady}
          onFirstDataRendered={onFirstDataRendered}
          onPaginationChanged={onPaginationChanged}
          onCellValueChanged={handleCellValueChanged}
          onCellEditingStopped={handleCellEditingStopped}
          // autoSizeStrategy={autoSizeStrategy}
          ensureDomOrder={true}
          suppressColumnVirtualisation={true}
          headerHeight={null}
          wrapHeaderText={true}
          autoHeaderHeight={true}
        />
      </div>
    </Box>
  );
}
